import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  Box,
  Grid,
  Stack,
  SvgIcon,
  Fab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import { ReactComponent as TrackIcon } from "../assets/IconTrack.svg";
import { ReactComponent as CurateIcon } from "../assets/IconCurate.svg";
import { ReactComponent as ExploreIcon } from "../assets/IconExplore.svg";
import { getStatusOptionsArray } from "../utils/utils";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import MobileInstallInstructions from "./MobileInstallInstructions";
import SearchIcon from "@mui/icons-material/Search";
import { useThemeContext } from "../context/themeContext";
import BetaPill from "./BetaPill";

const HelpModal = ({ open, onClose, mode }) => {
  const { isTouchDevice } = useThemeContext();
  const statuses = getStatusOptionsArray();
  const theme = useTheme();
  const fullscreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullscreen}
      position="relative"
    >
      <DialogTitle sx={{ mr: 3 }}>
        {mode === "installation" ? "" : "How it all works"}
      </DialogTitle>
      {open && (
        <DialogContent>
          <Grid container spacing={2}>
            {mode !== "installation" && (
              <>
                <Grid item xs={12} sm={3}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={{ xs: "center", sm: "flex-start" }}
                    spacing={"-6px"}
                    sx={{ mt: fullscreen ? 2 : 0 }}
                  >
                    <Fab
                      color="primary"
                      aria-label="add"
                      sx={{
                        zIndex: 0, // ✅ lower than default (which is 1050+)
                        pointerEvents: "none", // ✅ ensures no hover or cursor styles
                        width: 50,
                        height: 50,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transform: "scale(1.3)",
                        }}
                      >
                        <SearchIcon />
                      </Box>
                    </Fab>
                    <Typography
                      variant="h5"
                      color="text.primary"
                      sx={{ zIndex: 1, pt: 0 }}
                    >
                      <b>Search</b>
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography paragraph>
                    Search for a movie, book, TV show or profile from anywhere
                    in the app at the press of a button - always located at the
                    bottom right of the screen.
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={{ xs: "center", sm: "flex-start" }}
                    spacing={-2}
                    sx={{ mt: fullscreen ? 2 : 0 }}
                  >
                    <SvgIcon
                      sx={{
                        width: 50,
                        height: 50,
                        color: "primary.main",
                      }}
                    >
                      <TrackIcon />
                    </SvgIcon>
                    <Typography variant="h5" color="text.primary">
                      <b>Track</b>
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography paragraph>
                    Any content you encounter throughout the app can be tracked
                    in a single click. Just {isTouchDevice ? "tap" : "hover on"}{" "}
                    its cover image to reveal the status buttons.
                  </Typography>
                  <Typography paragraph>
                    If it's already tracked, the cover image will be outlined
                    with the colour of its status.
                  </Typography>
                  <Typography>
                    All tracked content appears on your track page segmented by
                    content type and status. The statuses are as follows:
                  </Typography>
                  <List sx={{ "& .MuiListItem-root": { py: 0 } }}>
                    {statuses
                      .filter((statusOption) => statusOption.value !== "none") // Filter out "none"
                      .map((statusOption) => (
                        <ListItem key={statusOption.value}>
                          <ListItemIcon>{statusOption.iconFilled}</ListItemIcon>
                          <ListItemText>{statusOption.label}</ListItemText>
                        </ListItem>
                      ))}
                  </List>
                  <Typography paragraph>
                    Your <strong>queue</strong> is your "to-watch and to-read"
                    list. The other statuses speak for themselves.
                  </Typography>
                  <Typography paragraph>
                    Each time you add or update a status, a journal entry is
                    created to capture your notes, watch/read date, rating
                    and/or reactions. You can only rate and react to content
                    marked as finished.
                  </Typography>
                  <Typography paragraph>
                    Journal entries with notes will appear in your followers'
                    explore feeds, unless you change the visibility to 'only
                    me'.
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={{ xs: "center", sm: "flex-start" }}
                    spacing={-2}
                    sx={{ mt: fullscreen ? 2 : 0 }}
                  >
                    <SvgIcon
                      sx={{
                        width: 50,
                        height: 50,
                        color: "primary.main",
                      }}
                    >
                      <CurateIcon />
                    </SvgIcon>
                    <Typography variant="h5" color="text.primary">
                      <b>Curate</b>
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography paragraph>
                    Your collections live here - ones you've built, and ones
                    you've saved from across the app.
                  </Typography>
                  <Typography>
                    Make them private, or share them with friends.
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={{ xs: "center", sm: "flex-start" }}
                    spacing={-2}
                    sx={{ mt: fullscreen ? 2 : 0 }}
                  >
                    <SvgIcon
                      sx={{
                        width: 50,
                        height: 50,
                        color: "primary.main",
                      }}
                    >
                      <ExploreIcon />
                    </SvgIcon>
                    <Typography variant="h5" color="text.primary">
                      <b>Explore</b> <BetaPill />
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography paragraph>
                    A curated space for discovering your next favourite film, TV
                    show or book. See what the people you follow have been
                    watching and reading. Browse handpicked collections of
                    critics' favourites, award winners, hidden gems and more.
                  </Typography>
                  <Typography>
                    This area’s still growing, but it’ll only get smarter and
                    more personalised as our community expands.
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={{ xs: "center", sm: "flex-start" }}
                    spacing={-1}
                    sx={{ mt: fullscreen ? 2 : 0 }}
                  >
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        bgcolor: (theme) => theme.palette.primary.main,
                        color: (theme) => theme.palette.primary.contrastText,
                        zIndex: 0,
                      }}
                    >
                      <PersonIcon fontSize="large" />
                    </Avatar>
                    <Typography
                      variant="h5"
                      color="text.primary"
                      sx={{ zIndex: 1 }}
                    >
                      <b>Profile</b>
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography paragraph>
                    Showcase your favourites to your followers. Populating your
                    all time top 5 picks is the best way to be matched with
                    accounts with similar tastes.{" "}
                    <Box
                      component="span"
                      sx={{ color: "tertiary.main", fontWeight: "bold" }}
                    >
                      Coming soon!
                    </Box>
                  </Typography>
                  <Typography>
                    Keep your profile fresh - monthly and yearly picks reset, so
                    your current favourites are always on display.
                  </Typography>
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={3}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent={{ xs: "center", sm: "flex-start" }}
                spacing={-1}
                sx={{ mt: fullscreen ? 2 : 0 }}
              >
                <Avatar
                  sx={{
                    width: 50,
                    height: 50,
                    bgcolor: (theme) => theme.palette.primary.main,
                    color: (theme) => theme.palette.primary.contrastText,
                    zIndex: 0,
                  }}
                >
                  <DownloadIcon fontSize="large" />
                </Avatar>
                <Typography
                  variant="h5"
                  color="text.primary"
                  sx={{ zIndex: 1 }}
                >
                  <b>Install</b>
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={9}>
              <MobileInstallInstructions />
            </Grid>
          </Grid>

          <Box textAlign="right" mt={1}>
            <Button onClick={onClose} variant="contained">
              Got it
            </Button>
          </Box>
        </DialogContent>
      )}

      <IconButton
        size="small"
        sx={{ position: "absolute", top: 12, right: 12 }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>
    </Dialog>
  );
};

export default HelpModal;
